import { useEffect, useMemo, useState } from 'react';

import type { Resource } from '@setvi/shared/interfaces';

import { buildPreviewPages } from '../utils';

interface UseCustomizePreviewArgs {
  resource: Resource;
  hasQrPreview: boolean;
  hasCoverPreview: boolean;
  coverPdfUrl?: string;
  coverPageCount: number;
  machines: string[];
  qrSheetUrl?: string;
}

/**
 * The pages shown beside the customization controls, and which one is on
 * screen. Built from whatever has actually been generated, so nothing is listed
 * that cannot be rendered.
 */
export const useCustomizePreview = ({
  resource,
  hasQrPreview,
  hasCoverPreview,
  coverPdfUrl,
  coverPageCount,
  machines,
  qrSheetUrl
}: UseCustomizePreviewArgs) => {
  const [page, setPage] = useState(0);

  const pages = useMemo(
    () =>
      buildPreviewPages({
        hasCoverPreview,
        hasQrPreview,
        coverPdfUrl,
        coverPageCount,
        machines,
        qrSheetUrl
      }),
    [
      hasCoverPreview,
      hasQrPreview,
      coverPdfUrl,
      coverPageCount,
      machines,
      qrSheetUrl
    ]
  );

  const pageIndex = Math.min(page, Math.max(0, pages.length - 1));

  // Keep the pager index valid when the number of pages changes.
  useEffect(() => {
    setPage(current => Math.min(current, Math.max(0, pages.length - 1)));
  }, [pages.length]);

  return {
    pages,
    pageIndex,
    hasPreview: pages.length > 0,
    setPage,
    /** QR machine pages start after the cover's. */
    qrPageOffset: hasCoverPreview ? Math.max(1, coverPageCount) : 0,
    // The active page's PDF, or the asset's own before anything is generated.
    document: {
      ...resource,
      DownloadURLWithSas:
        pages[pageIndex]?.pdfUrl ?? resource.DownloadURLWithSas
    }
  };
};
